import { GCLOUD_SECURITY_PERIMETER_FEATURE_FLAG } from "@p0security/integrations-gcloud-shared/constants";
import type { GcloudIntegration } from "@p0security/integrations-gcloud-shared/types";
import { SINGLETON_ITEM } from "@p0security/shared/install/constants";
import { installedItem } from "@p0security/shared/install/installed";
import { otherInstalled } from "@p0security/shared/install/options";
import { Typography } from "@p0security/ui";
import { Link } from "react-router-dom";

import { gcloudAccessLogsInstructor } from "./access-logs";
import { functionCallerInstructions } from "./function-caller";
import { iamInstructor, iamPrerequisiteMessage } from "./iam";
import { orgWidePolicyInstructor } from "./org-wide-policy";
import { sharingRestrictionWarningLabeler } from "./root";
import {
  cloudRunUrlInstaller,
  securityPerimeterInstructions,
} from "./security-perimeter";
import { sharingRestrictionInstructions } from "./sharing-restriction";
import type { GcloudInstaller } from "./types";

export const gcloudInstaller: GcloudInstaller = {
  components: {
    root: {
      items: {
        organizationId: {
          labeler: sharingRestrictionWarningLabeler,
        },
      },
    },
    "access-logs": {
      instructions: gcloudAccessLogsInstructor("project"),
      optionProvider: otherInstalled<GcloudIntegration>("iam-assessment"),
      prerequisiteMessages: async (context) => {
        if (installedItem("org-access-logs", context.config, SINGLETON_ITEM))
          return (
            <div>
              Cannot install project access logs with active organization access
              log installation
            </div>
          );
      },
      items: {},
    },
    "iam-assessment": {
      instructions: iamInstructor("project", "iam-assessment"),
    },
    "iam-write": {
      prerequisiteMessages: iamPrerequisiteMessage,
      instructions: (context, id) => {
        return context.featureFlags?.[GCLOUD_SECURITY_PERIMETER_FEATURE_FLAG]
          ? iamInstructor("project", "iam-write-2")(context, id)
          : iamInstructor("project", "iam-write")(context, id);
      },
    },
    "org-access-logs": {
      instructions: gcloudAccessLogsInstructor("organization"),
      items: {},
    },
    "org-iam-assessment": {
      instructions: iamInstructor("organization", "iam-assessment"),
    },
    "org-wide-policy": {
      instructions: orgWidePolicyInstructor,
    },
    // This item is hidden
    "key-rotation": { items: {} },
    "sharing-restriction": {
      instructions: sharingRestrictionInstructions,
      optionProvider: otherInstalled<GcloudIntegration>("iam-write"),
    },
    "function-caller": {
      labeler: () => (
        <>Function and container deployment types are supported.</>
      ),
      instructions: functionCallerInstructions,
      items: {},
    },
    "iam-write-security-perimeter": {
      labeler: () => (
        <>
          Project identifier, in your environment, where you will install
          P0&apos;s security perimeter
        </>
      ),
      instructions: securityPerimeterInstructions(
        "iam-write-security-perimeter"
      ),
      items: {
        cloudRunUrl: cloudRunUrlInstaller,
      },
    },
  },
  preamble: (
    <>
      <Typography.Text strong>
        Recommended components by use case:
      </Typography.Text>
      <ul className="preamble-list">
        <li>
          <strong>Just-in-time access:</strong>{" "}
          <Link to="./iam-write-security-perimeter">Security Perimeter</Link>,
          then <Link to="./iam-write">IAM Management</Link>
        </li>
        <li>
          <strong>Inventory and Posture:</strong>{" "}
          <Link to="./iam-assessment">IAM Assessment</Link> or{" "}
          <Link to="./org-iam-assessment">Organization IAM Assessment</Link>,
          and optionally <Link to="./access-logs">Access Logging</Link>
        </li>
        <li>
          <strong>Custom notifiers:</strong>{" "}
          <Link to="./function-caller">Cloud Run Invocation</Link>
        </li>
      </ul>
    </>
  ),
};
